import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'
import { CircleMinusIcon, PlusIcon } from 'lucide-react'
import React from 'react'
import { CaseComplainantTypesProvider } from '../hooks/case-complainants-types'

type SelectTypeOption<T> = T extends { id: number; name: string } ? T : never

export interface SelectTypeButtonProps<T> {
  label: string
  options: SelectTypeOption<T>[]
  value: SelectTypeOption<T> | null
  valueIcon: React.ReactNode
  onChange?: (value: SelectTypeOption<T> | null) => void
}

export default function SelectTypeButton<T>({
  ...props
}: SelectTypeButtonProps<T> &
  Omit<
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    keyof SelectTypeButtonProps<T>
  >) {
  return (
    <CaseComplainantTypesProvider>
      <Inner<T> {...props} />
    </CaseComplainantTypesProvider>
  )
}

function Inner<T>({
  label,
  options,
  value,
  valueIcon,
  onChange,
  children,
  className,
  ...props
}: SelectTypeButtonProps<T> &
  Omit<
    React.HTMLAttributes<HTMLButtonElement>,
    keyof SelectTypeButtonProps<T>
  >) {
  return (
    <DropdownMenu>
      {/* Disable tooltip when no value is selected, the label is descriptive enough */}
      <Tooltip open={!value ? false : undefined}>
        <TooltipTrigger asChild>
          <DropdownMenuTrigger asChild>
            {children || (
              <Button
                size="sm"
                variant="outline"
                className={cn('h-8 text-sm', className)}
                {...props}
              >
                {value ? valueIcon : <PlusIcon />}
                {value?.name || label}
              </Button>
            )}
          </DropdownMenuTrigger>
        </TooltipTrigger>
        <TooltipContent>{label}</TooltipContent>
      </Tooltip>
      <DropdownMenuContent align="start">
        <DropdownMenuLabel className="flex items-center justify-between">
          {label}
          {!!value && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="xs"
                  onClick={() => onChange?.(null)}
                >
                  <CircleMinusIcon className="text-destructive" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Remove {label}</TooltipContent>
            </Tooltip>
          )}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {options.map((opt) => (
          <DropdownMenuItem
            className={
              opt.id === value?.id ? 'bg-accent text-accent-foreground' : ''
            }
            key={opt.id}
            onSelect={() => onChange?.(opt)}
          >
            {opt.name}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
